"use client"

import { X, User, CreditCard, Database, Monitor, Settings as SettingsIcon, Users, Cpu, BarChart2, Activity } from "lucide-react"
import { useLocale } from "@/contexts/LocaleContext"

import ProfilePage from "@/app/settings/profile/page"
import BillingPage from "@/app/settings/billing/page"
import DataPage from "@/app/settings/data/page"
import DisplaySettingsPage from "@/app/settings/display/page"
import WorkspacePage from "@/app/settings/workspace/page"
//import PanelSetPage from "@/app/settings/panelset/page"
//import ModelsSettingsPage from "@/app/settings/models/page"
import UsagePage from "@/app/settings/usage/page"
import StatsPage from "@/app/settings/stats/page"
import TokensPage from "@/app/settings/tokens/page"

//export type SettingsSection = "profile" | "billing" | "data" | "display" | "workspace" | "panelset" | "models" | "usage" | "stats" | "tokens"
export type SettingsSection = "profile" | "billing" | "data" | "display" | "workspace" | "usage" | "stats" | "tokens"

interface SettingsModalProps {
  section: SettingsSection | null
  onClose: () => void
  onSectionChange: (s: SettingsSection) => void
}

export default function SettingsModal({ section, onClose, onSectionChange }: SettingsModalProps) {
  const { t } = useLocale()

  if (!section) return null

  const navItems: { id: SettingsSection; icon: React.ReactNode; label: string }[] = [
    { id: "profile",   icon: <User size={15} />,         label: t("nav.profile") },
    { id: "billing",   icon: <CreditCard size={15} />,   label: t("nav.billing") },
    { id: "data",      icon: <Database size={15} />,     label: t("nav.data") },
    { id: "display",   icon: <Monitor size={15} />,      label: t("nav.display") },
    { id: "workspace", icon: <SettingsIcon size={15} />, label: t("nav.workspace") },
    //{ id: "panelset",  icon: <Users size={15} />,        label: t("nav.panelSet") },
    //{ id: "models",    icon: <Cpu size={15} />,          label: t("nav.aiModels") },
    { id: "usage",     icon: <BarChart2 size={15} />,    label: t("nav.usage") },
    { id: "stats",     icon: <Activity size={15} />,     label: t("nav.stats") },
    { id: "tokens",    icon: <BarChart2 size={15} />,    label: t("nav.tokens") },
  ]

  const renderContent = () => {
    switch (section) {
      case "profile":   return <ProfilePage />
      case "billing":   return <BillingPage />
      case "data":      return <DataPage />
      case "display":   return <DisplaySettingsPage />
      case "workspace": return <WorkspacePage />
      //case "panelset":  return <PanelSetPage />
      //case "models":    return <ModelsSettingsPage />
      case "usage":     return <UsagePage />
      case "stats":     return <StatsPage />
      case "tokens":    return <TokensPage />
      default:          return null
    }
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-[#0d0d14] border border-white/[0.08] rounded-2xl w-[90vw] max-w-3xl h-[85vh] shadow-2xl flex overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 좌측 네비 */}
        <div className="w-52 shrink-0 border-r border-white/[0.06] p-4 space-y-1 overflow-y-auto">
          <p className="text-[10px] font-semibold text-white/20 tracking-widest uppercase px-2 mb-3">
            {t("nav.sectionSettings")}
          </p>
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => onSectionChange(item.id)}
              className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left transition-colors ${
                section === item.id
                  ? "bg-purple-600/20 text-purple-300"
                  : "text-white/40 hover:text-white/70 hover:bg-white/[0.04]"
              }`}
            >
              <span className={section === item.id ? "text-purple-400" : ""}>{item.icon}</span>
              <span className="text-xs font-medium">{item.label}</span>
            </button>
          ))}
        </div>

        {/* 우측 콘텐츠 — 기존 /settings/* 페이지 컴포넌트를 그대로 렌더링 */}
        <div className="flex-1 overflow-y-auto relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 text-white/30 hover:text-white/70 transition-colors"
          >
            <X size={18} />
          </button>
          {renderContent()}
        </div>
      </div>
    </div>
  )
}